import { Injectable } from '@angular/core';
import { CUSTOMAPIService } from 'app/custom-api.service';
import {
  _GlobalControlItem,
  GC_SUFFIX,
  GlobalControlsConnectionsExtended,
  LogEntry,
  LOGS_DELIMITER,
} from './global-controls.constant';
import { v4 as uuid } from 'uuid';
import { EntityTypeEnum, GetUserQuery, GlobalControlsConnections } from 'app/API.service';
import { UserService } from 'app/shared/user.service';
import { LogsTypeEnum } from 'app/shared/enums/logsKey.enum';

@Injectable({
  providedIn: 'root',
})
export class GlobalControlService {
  private currentUser: GetUserQuery;

  constructor(
    private customAPI: CUSTOMAPIService,
    private userService: UserService
  ) {
    this.initializeCurrentUser();
  }

  async initializeCurrentUser() {
    try {
      this.currentUser = await this.userService.getCurrentUser();
    } catch (error) {
      console.error('Error initializing current user:', error);
      throw error;
    }
  }

  /**
   * Function to add global controls to the specified root entity.
   * It creates connections between the origin and destination global control items.
   * @param rootEntity Root Entity ID
   * @param selectedOrigin Selected Origin Global Control Item
   * @param selectedDestinationList Destination Global Control Items
   * @returns Promise resolving to the response of the API call
   */
  async addGlobalControlConnections(
    rootEntity: string,
    selectedOrigin: _GlobalControlItem,
    selectedDestinationList: _GlobalControlItem[]
  ) {
    try {
      const newGlobalControls = [];
      for (const destination of selectedDestinationList) {
        const payload = {
          id: uuid(),
          rootID: rootEntity,
          originSubEntityId: selectedOrigin.subEntityId,
          originAssessmentStandardFrameworkId: selectedOrigin.id,
          destinationSubEntityId: destination.subEntityId,
          destinationAssessmentStandardFrameworkId: destination.id,
          isConnectionValid: true,
        };
        const response = (await this.customAPI.CreateGlobalControlsConnections(payload)) as any;
        if (response?.errors && response.errors.length > 0) {
          throw new Error('Failed to create global control connection');
        } else {
          newGlobalControls.push(response);
          await this.createLogs(rootEntity, selectedOrigin.displayName, destination.displayName);
        }
      }
      return newGlobalControls;
    } catch (error) {
      console.error('Error adding global controls:', error);
    }
  }

  /**
   * Function to fetch global control connections by root entity.
   * @param rootEntity Root Entity ID
   * @returns  an array of GlobalControlsConnections
   */
  async getGlobalConnectionsByRootEntity(rootEntity: string) {
    try {
      let globalControlList: GlobalControlsConnections[] = [];
      let nextToken = null;
      do {
        const globalControlResponse = await this.customAPI.GlobalControlsByRootId(rootEntity);
        globalControlList = globalControlList.concat(globalControlResponse.items as any);
        nextToken = globalControlResponse.nextToken;
      } while (nextToken);

      return globalControlList;
    } catch (e) {
      console.error('Error fetching entities with frameworks by parent ID:', e);
      return Promise.reject(e);
    }
  }

  /**
   * Function to delete a global control connection.
   * @param connection GlobalControlsConnectionsExtended that needs to be deleted
   * @returns response of the delete operation
   */
  async deleteGlobalControlConnection(connection: GlobalControlsConnectionsExtended, rootId: string) {
    try {
      const response = (await this.customAPI.DeleteGlobalControlsConnections(
        { id: connection.id },
        { rootID: { eq: rootId } }
      )) as any;
      if (response?.errors && response.errors.length > 0) {
        throw new Error('Failed to delete global control connection');
      } else {
        await this.createLogs(connection.rootID, connection.originName, connection.destinationName, true);
        return response;
      }
    } catch (error) {
      console.error('Error deleting global control connection:', error);
      return Promise.reject(error);
    }
  }

  /**
   * Function to create logs for global control operations.
   * @param rootEntity Root Entity ID
   * @param origin Origin Name
   * @param destination Destination Name
   * @param isDelete flag to indicate if this is a delete operation
   */
  async createLogs(rootEntity: string, origin: string, destination: string, isDelete: boolean = false) {
    try {
      await this.customAPI.CreateLogs({
        id: uuid(),
        message: `${origin}${LOGS_DELIMITER}${destination}`,
        type: isDelete ? LogsTypeEnum.GLOBAL_CONTROL_DELETE : LogsTypeEnum.GLOBAL_CONTROL_CREATE,
        targetId: `${rootEntity}#${GC_SUFFIX}`,
        userID: this.currentUser.id,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getLogsByTargetId(targetId: string) {
    try {
      let logsList = [];
      let nextToken = null;
      do {
        const logsResponse = await this.customAPI.LogsByTargetId(targetId, null, null, 1000, nextToken);
        logsList = logsList.concat(logsResponse.items as any);
        nextToken = logsResponse.nextToken;
      } while (nextToken);
      //* sort logs by time
      logsList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      logsList = this.formatLogs(logsList);
      return logsList;
    } catch (error) {
      console.error('Error fetching logs by target ID:', error);
      return Promise.reject(error);
    }
  }

  // ******************************* Helper Functions ********************************

  private formatLogs(logs: any[]): LogEntry[] {
    return logs.map(log => ({
      id: log.id,
      action: log.type === LogsTypeEnum.GLOBAL_CONTROL_CREATE ? 'Created' : 'Deleted',
      userName: log?.user?.name || 'Unknown User',
      date: new Date(log.createdAt).toUTCString(),
      origin: log.message ? log.message.split(LOGS_DELIMITER)[0] : '',
      destination: log.message ? log.message.split(LOGS_DELIMITER)[1] : '',
    }));
  }

  //* Get valid risk framework from the list of standards
  //* Returns the first valid risk framework or null if none found
  private getValidRiskFramework(standards: any[]): any {
    const validRiskFramework =
      standards.filter(
        (framework: any) =>
          framework.type === EntityTypeEnum.RISK_FRAMEWORK &&
          (framework.not_added === null || framework.not_added === false) &&
          (framework.archived === null || framework.archived === false)
      )[0] || null;
    return validRiskFramework;
  }

  //* format sub-entity list from entities data to display in the UI
  formatSubEntityList(entitiesData: any[]): _GlobalControlItem[] {
    try {
      const results: _GlobalControlItem[] = [];
      entitiesData.forEach((entity: any) => {
        if (!entity?.activeAssessment?.standardFrameworkList?.items) {
          return;
        }
        const standards = entity.activeAssessment.standardFrameworkList.items;
        const validRiskFramework = this.getValidRiskFramework(standards);
        if (!validRiskFramework) {
          return;
        }
        results.push({
          id: validRiskFramework.id,
          displayName: `${entity.name.toUpperCase()} | ${validRiskFramework.key?.split('#')[0].replace(/_/g, ' ')}`,
          subEntityId: entity.id,
        });
      });
      return results || [];
    } catch (error) {
      console.error('Error initializing sub-entity list:', error);
    }
  }

  //* get formatted name for origin or destination
  //* Returns a string combining the entity name and framework
  private getFormattedName(object: GlobalControlsConnections, isOrigin: boolean = true): string {
    if (!object) {
      return '';
    }
    const entityName = isOrigin ? object.originSubEntity?.name : object.destinationSubEntity?.name || '';
    const framework = isOrigin
      ? object?.originAssessmentStandardFrameworkId
      : object?.destinationAssessmentStandardFrameworkId || '';

    return object ? `${entityName.toUpperCase()} | ${framework.split('#')[1].replace(/_/g, ' ')}` : '';
  }

  //* Map global controls to include formatted names for origin and destination
  //* Returns an array of GlobalControlsConnectionsExtended with additional properties
  mapOriginAndDestination(globalCOntrols: GlobalControlsConnections[]): GlobalControlsConnectionsExtended[] {
    return globalCOntrols.map(item => {
      return {
        ...item,
        originName: this.getFormattedName(item, true),
        destinationName: this.getFormattedName(item, false),
      };
    });
  }
}
